import type { AxiosError } from 'axios';
import type React from 'react';
import i18n from '@/lib/i18n';
import { I18nNamespace } from '@/lib/i18n-namespaces';

export interface ApiError {
    message?: string;
    detail?: unknown;
    error?: string;
    errors?: Record<string, string[]>;
    limit?: number;
    resource?: string;
}

const isAxiosError = (error: unknown): boolean => {
    return (
        typeof error === 'object' &&
        error !== null &&
        'isAxiosError' in error &&
        Boolean((error as { isAxiosError?: boolean }).isAxiosError)
    );
};

const TRANSLATABLE_ERROR_PREFIXES = ['errors.organization.', 'errors.license.'];

const UNKNOWN_LIMIT_DETAILS = new Set([
    'errors.organization.users_limit_reached',
    'errors.organization.cameras_limit_reached',
    'errors.organization.alert_units_limit_reached',
]);

const getTranslatedDetail = (
    detail: string,
    interpolation?: Record<string, unknown>,
): string => {
    if (
        !TRANSLATABLE_ERROR_PREFIXES.some((prefix) => detail.startsWith(prefix))
    ) {
        return detail;
    }
    const key =
        interpolation?.max === undefined && UNKNOWN_LIMIT_DETAILS.has(detail)
            ? `${detail}_unknown`
            : detail;
    return i18n.t(`settings:${key}`, interpolation);
};

export const getApiErrorMessage = (
    error: unknown,
    fallbackMessage: string = i18n.t('errors.unexpected', {
        ns: I18nNamespace.COMMON,
    }),
): string => {
    if (isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiError>;
        const data = axiosError.response?.data;

        if (data) {
            if (typeof data.detail === 'string' && data.detail)
                return getTranslatedDetail(
                    data.detail,
                    data.limit !== undefined ? { max: data.limit } : undefined,
                );
            if (data.message) return data.message;
            if (data.error) return data.error;

            if (data.errors) {
                const errorMessages = Object.entries(data.errors)
                    .map(
                        ([field, messages]) =>
                            `${field}: ${messages.join(', ')}`,
                    )
                    .join('; ');
                if (errorMessages) return errorMessages;
            }
        }

        return fallbackMessage;
    }

    if (typeof error === 'string') {
        return error;
    }

    return fallbackMessage;
};

export const MEDIA_ERROR_TOAST_ID = 'media-error';

export const translateMediaError = (
    key: string,
    interpolation?: Record<string, unknown>,
): string =>
    i18n.t(`media.errors.${key}`, {
        ns: I18nNamespace.COMMON,
        ...interpolation,
    });

const MEDIA_CONTENT_TYPE_PATTERN =
    /^(video\/|audio\/|application\/(vnd\.apple\.mpegurl|x-mpegurl|dash\+xml))/i;

const OPAQUE_CONTENT_TYPE_PATTERN = /^application\/octet-stream/i;

const probeMedia = (src: string): Promise<Response> =>
    fetch(src, {
        cache: 'no-store',
        credentials: 'include',
        headers: { Range: 'bytes=0-0' },
    });

export const isMediaUnauthorized = async (src: string): Promise<boolean> => {
    try {
        return (await probeMedia(src)).status === 401;
    } catch {
        return false;
    }
};

const describeMediaLoadFailure = async (src: string): Promise<string> => {
    try {
        const response = await probeMedia(src);
        if (response.status === 401 || response.status === 403) {
            return translateMediaError('accessDenied');
        }
        if (response.status === 404) {
            return translateMediaError('fileNotFound');
        }
        if (!response.ok) {
            return translateMediaError('serverError', {
                status: response.status,
            });
        }
        const contentType = response.headers.get('Content-Type') ?? '';
        if (OPAQUE_CONTENT_TYPE_PATTERN.test(contentType)) {
            return translateMediaError('missingContentType');
        }
        if (!MEDIA_CONTENT_TYPE_PATTERN.test(contentType)) {
            return translateMediaError('notMedia');
        }
        return translateMediaError('unsupportedFormat');
    } catch {
        return translateMediaError('networkError');
    }
};

export const getMediaErrorMessage = async (
    error: Event | React.SyntheticEvent | { target?: HTMLElement },
): Promise<string | null> => {
    const target =
        (error as Event | React.SyntheticEvent).target ||
        (error as { target?: HTMLElement }).target;

    if (!target) return null;

    if (target instanceof HTMLImageElement) {
        if (target.src && target.src.includes('/api/storage/')) {
            return translateMediaError('imageAccessDeniedOrNotFound');
        }
        return translateMediaError('imageLoadFailed');
    }

    if (
        !(target instanceof HTMLVideoElement) &&
        !(target instanceof HTMLAudioElement)
    ) {
        return null;
    }

    const errorCode = target.error?.code;

    if (errorCode === MediaError.MEDIA_ERR_ABORTED) {
        return null;
    }
    if (errorCode === MediaError.MEDIA_ERR_DECODE) {
        return translateMediaError('decodingError');
    }
    if (!target.src) {
        return translateMediaError('loadFailed');
    }
    return await describeMediaLoadFailure(target.src);
};
